import type {
  AdminAddMembersRequest,
  AdminChannel,
  AdminCreateChannelRequest,
  AdminUpdateChannelRequest,
  Conversation,
  CreateConversationRequest,
  CreatePollRequest,
  EditMessageRequest,
  LoginRequest,
  LoginResponse,
  Message,
  SessionInfo,
  SendMessageRequest,
  UpdateMeRequest,
  User,
} from "@swift/shared";
import { loadSettings } from "./settings";
import { isTauriSync } from "./tauri";
import { isCapacitor } from "./capacitor";

class ApiError extends Error {
  constructor(public status: number, public code: string) {
    super(`${status} ${code}`);
  }
}

function joinUrl(base: string, path: string): string {
  if (!base) throw new Error("Server URL ontbreekt");
  return base.replace(/\/$/, "") + path;
}

/** Leest de swift_csrf-cookie. Alleen relevant in PWA-mode — Tauri-mode
 *  gebruikt geen cookies. Cookie is bewust niet-httpOnly zodat JS 'm kan
 *  lezen voor de double-submit pattern. */
function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === "swift_csrf") return part.slice(eq + 1).trim();
  }
  return null;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string | null,
): Promise<T> {
  const { server_url, token: stored } = loadSettings();
  // Tauri-MSI én Android-Capacitor-APK gebruiken Bearer-token-auth uit
  // localStorage. PWA-browser-mode gebruikt cookie-auth (httpOnly) +
  // CSRF-double-submit. De server accepteert beide formaten via
  // authGuard.
  const native = isTauriSync() || isCapacitor();
  const auth = native ? token ?? stored : null;
  const headers: Record<string, string> = {};
  if (auth) headers.Authorization = `Bearer ${auth}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!native && !SAFE_METHODS.has(method)) {
    const csrf = readCsrfCookie();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  const res = await fetch(joinUrl(server_url, path), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: native ? "omit" : "include",
  });
  if (res.status === 204) return undefined as unknown as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (data as { error?: string }).error ?? "error");
  }
  return data as T;
}

export const api = {
  login: (req: LoginRequest) => request<LoginResponse>("POST", "/api/v1/auth/login", req),
  logout: () => request<void>("POST", "/api/v1/auth/logout"),
  me: () => request<{ user: User }>("GET", "/api/v1/me"),
  updateMe: (req: UpdateMeRequest) => request<{ user: User }>("PATCH", "/api/v1/me", req),
  listUsers: () => request<{ users: User[] }>("GET", "/api/v1/users"),
  listConversations: () => request<{ conversations: Conversation[] }>("GET", "/api/v1/conversations"),
  createConversation: (req: CreateConversationRequest) =>
    request<{ conversation: Conversation }>("POST", "/api/v1/conversations", req),
  listMessages: (conversationId: number, before?: number, limit = 50) => {
    const params = new URLSearchParams();
    if (before) params.set("before", String(before));
    params.set("limit", String(limit));
    return request<{ messages: Message[] }>(
      "GET",
      `/api/v1/conversations/${conversationId}/messages?${params.toString()}`,
    );
  },
  sendMessage: (conversationId: number, req: SendMessageRequest) =>
    request<{ message: Message }>(
      "POST",
      `/api/v1/conversations/${conversationId}/messages`,
      req,
    ),
  editMessage: (messageId: number, req: EditMessageRequest) =>
    request<{ message: Message }>("PATCH", `/api/v1/messages/${messageId}`, req),
  deleteMessage: (messageId: number) =>
    request<void>("DELETE", `/api/v1/messages/${messageId}`),
  addReaction: (messageId: number, emoji: string) =>
    request<void>("POST", `/api/v1/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId: number, emoji: string) =>
    request<void>("DELETE", `/api/v1/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`),
  search: (q: string, conversation?: number) => {
    const params = new URLSearchParams({ q });
    if (conversation) params.set("conversation", String(conversation));
    return request<{
      results: Array<{
        id: number;
        conversation_id: number;
        sender_id: number;
        body: string;
        created_at: string;
      }>;
    }>("GET", `/api/v1/search?${params.toString()}`);
  },

  // ── Sessies ─────────────────────────────────────────────────────
  listSessions: () => request<{ sessions: SessionInfo[] }>("GET", "/api/v1/auth/sessions"),
  revokeSession: (prefix: string) =>
    request<void>("DELETE", `/api/v1/auth/sessions/${encodeURIComponent(prefix)}`),

  // ── Admin: kanaalbeheer ────────────────────────────────────────
  adminListChannels: () =>
    request<{ channels: AdminChannel[] }>("GET", "/api/v1/admin/channels"),
  adminCreateChannel: (req: AdminCreateChannelRequest) =>
    request<{ conversation: Conversation }>("POST", "/api/v1/admin/channels", req),
  adminUpdateChannel: (id: number, req: AdminUpdateChannelRequest) =>
    request<{ conversation: Conversation }>("PATCH", `/api/v1/admin/channels/${id}`, req),
  adminDeleteChannel: (id: number) =>
    request<void>("DELETE", `/api/v1/admin/channels/${id}`),
  adminAddMembers: (id: number, req: AdminAddMembersRequest) =>
    request<void>("POST", `/api/v1/admin/channels/${id}/members`, req),
  adminRemoveMember: (id: number, userId: number) =>
    request<void>("DELETE", `/api/v1/admin/channels/${id}/members/${userId}`),

  // ── Notificatie-instelling per gesprek (server-side mute) ─────────
  setConversationMuted: (conversationId: number, muted: boolean) =>
    request<void>("PATCH", `/api/v1/conversations/${conversationId}/notifications`, {
      muted,
    }),

  // ── Bestandsbeheer per kanaal ──────────────────────────────────────
  listConversationAttachments: (
    conversationId: number,
    kind?: "image" | "video" | "audio" | "file",
    beforeId?: number,
    limit = 50,
  ) => {
    const p = new URLSearchParams();
    if (kind) p.set("kind", kind);
    if (beforeId) p.set("before", String(beforeId));
    p.set("limit", String(limit));
    return request<{
      attachments: Array<{
        id: number;
        filename: string;
        mime: string;
        size: number;
        url: string;
        message_id: number;
        conversation_id: number;
        uploader_id: number;
        created_at: string;
      }>;
    }>("GET", `/api/v1/conversations/${conversationId}/attachments?${p.toString()}`);
  },

  // ── Pinnen (alleen in kanalen) ─────────────────────────────────────
  pinMessage: (messageId: number) =>
    request<{ message: Message }>("POST", `/api/v1/messages/${messageId}/pin`),
  unpinMessage: (messageId: number) =>
    request<void>("DELETE", `/api/v1/messages/${messageId}/pin`),
  listPinned: (conversationId: number) =>
    request<{ messages: Message[] }>(
      "GET",
      `/api/v1/conversations/${conversationId}/pinned`,
    ),

  // ── Mention-lade ────────────────────────────────────────────────────
  listMyMentions: (limit = 50) =>
    request<{ messages: Message[] }>(
      "GET",
      `/api/v1/me/mentions?limit=${limit}`,
    ),

  // ── Polls ───────────────────────────────────────────────────────────
  createPoll: (conversationId: number, req: CreatePollRequest) =>
    request<{ message: Message }>(
      "POST",
      `/api/v1/conversations/${conversationId}/polls`,
      req,
    ),
  votePoll: (pollId: number, optionIds: number[]) =>
    request<{ message: Message }>("POST", `/api/v1/polls/${pollId}/votes`, {
      option_ids: optionIds,
    }),
  clearMyPollVote: (pollId: number) =>
    request<{ message: Message }>("DELETE", `/api/v1/polls/${pollId}/votes`),
  closePoll: (pollId: number) =>
    request<{ message: Message }>("POST", `/api/v1/polls/${pollId}/close`),

  // ── Presence heartbeat (PWA achtergrond-tap) ──────────────────────
  presencePing: () => request<void>("POST", "/api/v1/me/presence-ping"),

  // ── Web Push ────────────────────────────────────────────────────
  getPushPublicKey: () =>
    request<{ vapid_public_key: string }>("GET", "/api/v1/push/public-key"),
  subscribePush: (req: {
    endpoint: string;
    p256dh: string;
    auth_key: string;
    device_label?: string;
  }) => request<void>("POST", "/api/v1/push/subscribe", req),
  unsubscribePush: (endpoint: string) =>
    request<void>("DELETE", "/api/v1/push/subscribe", { endpoint }),

  // ── FCM (Android-app via Capacitor) ─────────────────────────────
  subscribeFcm: (req: { fcm_token: string; device_label?: string }) =>
    request<void>("POST", "/api/v1/push/fcm/subscribe", req),
  unsubscribeFcm: (fcmToken: string) =>
    request<void>("DELETE", "/api/v1/push/fcm/subscribe", { fcm_token: fcmToken }),
};

export async function uploadFile(file: File): Promise<{
  attachment: { id: number; filename: string; mime: string; size: number; url: string };
}> {
  const { server_url, token } = loadSettings();
  const native = isTauriSync() || isCapacitor();
  const form = new FormData();
  form.append("file", file, file.name);
  const headers: Record<string, string> = {};
  if (native && token) headers.Authorization = `Bearer ${token}`;
  if (!native) {
    const csrf = readCsrfCookie();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  const res = await fetch(joinUrl(server_url, "/api/v1/uploads"), {
    method: "POST",
    headers,
    body: form,
    credentials: native ? "omit" : "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (err as { error?: string }).error ?? "upload_failed");
  }
  return res.json();
}

export { ApiError };

/** Haalt een bestaand attachment opnieuw op als `File`-object zodat het
 *  via de gewone upload-flow doorgestuurd kan worden naar een ander
 *  gesprek. Server-side koppelt een bijlage aan precies één bericht,
 *  dus voor doorsturen moeten we het bestand opnieuw inleveren. */
export async function attachmentToFile(att: {
  filename: string;
  mime: string;
  url: string;
}): Promise<File> {
  const { server_url, token } = loadSettings();
  const native = isTauriSync() || isCapacitor();
  const headers: Record<string, string> = {};
  if (native && token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(joinUrl(server_url, att.url), {
    headers,
    credentials: native ? "omit" : "include",
  });
  if (!res.ok) throw new ApiError(res.status, "attachment_fetch_failed");
  const blob = await res.blob();
  return new File([blob], att.filename, { type: att.mime || blob.type });
}
